import {
  endOfWeek as dfEndOfWeek,
  getISOWeek,
  startOfWeek as dfStartOfWeek,
} from 'date-fns';
import { nb } from 'date-fns/locale/nb';

/**
 * nb-NO Intl formatters (Plan 01-10 Task 3).
 *
 * Every display surface in the app flows through these helpers. Direct
 * `new Intl.*Format` constructions in components are forbidden (tree-shake
 * the shared instances here; avoid per-render allocation).
 *
 * HIGH-4 traps these helpers explicitly resolve:
 *   - 24-hour time, never AM/PM
 *   - thousand separator is U+00A0 NBSP (`Intl.NumberFormat('nb-NO')`)
 *   - decimal separator is comma
 *   - ISO week numbering (FND-08)
 *   - Monday-start week (FND-08)
 *   - Norwegian collation: Æ, Ø, Å sort AFTER Z
 *
 * All formatters take an explicit Date / number / object. They never call
 * `new Date()` internally — callers pass in the value.
 */

// Shared Intl instances. Allocation is non-trivial; keep module-level.
const DATE_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const DATE_SHORT = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const DATE_LONG = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const TIME_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const WEEKDAY_FORMAT = new Intl.DateTimeFormat('nb-NO', { weekday: 'long' });
const MONTH_SHORT_FORMAT = new Intl.DateTimeFormat('nb-NO', { month: 'short' });
const NUMBER_FMT = new Intl.NumberFormat('nb-NO');
const DECIMAL_1 = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const COLLATOR_NB = new Intl.Collator('nb');

/** "onsdag 22. april" */
export const formatDate = (d: Date): string => DATE_FORMAT.format(d);

/** "onsdag" */
export const formatWeekday = (d: Date): string => WEEKDAY_FORMAT.format(d);

/** "apr" — uten punktumet Intl setter etter forkortelsen (#154, «I dag»-flisen). */
export const formatMonthShort = (d: Date): string =>
  MONTH_SHORT_FORMAT.format(d).replace(/\.$/, '');

/** "Onsdag 22. april" — stor forbokstav der datoen innleder en linje (#154). */
export const capitalizeFirst = (s: string): string =>
  s.charAt(0).toLocaleUpperCase('nb-NO') + s.slice(1);

/** "22.04.2026" */
export const formatDateShort = (d: Date): string => DATE_SHORT.format(d);

/** "onsdag 22. april 2026" */
export const formatDateLong = (d: Date): string => DATE_LONG.format(d);

/** "16:42" — 24-hour, never AM/PM (HIGH-4). */
export const formatTime = (d: Date): string => {
  // `Intl.DateTimeFormat('nb-NO', { hour12: false })` can still emit "24" at
  // midnight on some engines. Normalise by constructing the string ourselves
  // when hour/minute parts are available.
  const parts = TIME_FORMAT.formatToParts(d);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const normalisedHour = hour === '24' ? '00' : hour;
  return `${normalisedHour}:${minute}`;
};

/** "16:42–17:15" — en dash between start + end, 24-hour. */
export const formatTimeRange = (start: Date, end: Date): string =>
  `${formatTime(start)}–${formatTime(end)}`;

/** ISO week number (FND-08). date-fns `getISOWeek` is Monday-anchored by spec. */
export const formatWeekNumber = (d: Date): string => getISOWeek(d).toString();

/** "1 250" — NBSP thousands separator (HIGH-4). */
export const formatPoints = (n: number): string => NUMBER_FMT.format(n);

/** "2,4" — comma decimal, 1 fraction digit (HIGH-4). */
export const formatDecimal = (n: number): string => DECIMAL_1.format(n);

/** Monday-anchored start-of-week (FND-08). */
export const startOfWeek = (d: Date): Date =>
  dfStartOfWeek(d, { weekStartsOn: 1, locale: nb });

/** Sunday-anchored end-of-week (Monday-start → ends on Sunday). */
export const endOfWeek = (d: Date): Date =>
  dfEndOfWeek(d, { weekStartsOn: 1, locale: nb });

/** Norwegian collation: Æ, Ø, Å sort after Z (HIGH-4). */
export const localeCompare = (a: string, b: string): number =>
  COLLATOR_NB.compare(a, b);

/**
 * Display name: nickname preferred, falling back to name.
 * Safety: trims whitespace; treats empty / null / undefined nickname as absent.
 */
export const formatMemberName = (m: {
  nickname?: string | null;
  name: string;
}): string => {
  const nick = (m.nickname ?? '').trim();
  if (nick.length > 0) return nick;
  return m.name.trim();
};

/**
 * Phase 3 D-14: event-card time range. Returns one of:
 *   "kl 16:30–17:30"   (timed, with end)
 *   "fra kl 16:30"     (timed, no end)
 *   "hele dagen"       (all-day)
 * The en-dash is U+2013 (matches formatTimeRange convention).
 */
export const formatEventTimeRange = (
  start: Date,
  end: Date | null,
  allDay: boolean,
): string => {
  if (allDay) return 'hele dagen';
  if (!end) return `fra kl ${formatTime(start)}`;
  return `kl ${formatTime(start)}–${formatTime(end)}`;
};

/**
 * Phase 3 D-14: Today + DayView header. Returns 'fredag 8. mai' (no year).
 * Wraps Intl.DateTimeFormat('nb-NO', { weekday:'long', day:'numeric', month:'long' }).
 */
const DAY_HEADER_FORMAT = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
export const formatDayHeader = (d: Date): string => DAY_HEADER_FORMAT.format(d);
